import { useState, type ReactNode } from "react";
import { useLocation } from "react-router";
import { postJson } from "../api";
import { useAiData, useTitle } from "../ai/parts";
import { Indicator, IndicatorSkeleton, Indicators, MobileActions, RowMenu, RowsSkeleton, Section } from "../ai/widgets";
import ui from "../ai/ai.module.css";
import { OrgNotFound } from "../pages/StatePages";
import { useOpenOrg } from "../shell/openOrg";
import { formatText, useTexts } from "../texts";
import { Notice, PageHead, useToast } from "../ui/kit";
import { PageState } from "../ui/pieces";
import {
  countryName,
  currencyName,
  displayName,
  languageName,
  moduleName,
  orgStatus,
  plural,
  timezoneLabel,
  whenIn,
  type OrgDetail,
} from "./organizations";
import { ConfirmWindow, ImpersonateWindow } from "./PersonActions";
import { CellSwitch, SimpleTable } from "./SimpleTable";
import styles from "./orgs.module.css";

type Module = { code: string; name: string; enabled: boolean; usersCount: number | null; locked: boolean };
type Modules = { usersAvailable: boolean; items: Module[] };

/**
 * `emp-resumen` · Empresas › {empresa} › Resumen (§5.3): encabezado con su estado, «Simular» y «Agregar usuario» (⋮ con
 * «Abrir como usuario…» solo si se puede); 4 indicadores con su destino; Módulos como tabla de ajustes (C38): apagar
 * confirma con los usuarios afectados (C12) y encender aplica con «Deshacer» (C13); «N usuarios» lleva a Usuarios
 * filtrado por ese módulo; y la Región como datos.
 */
export function OrganizationPage({ id }: { id: number }) {
  const { L, locale } = useTexts();
  const toast = useToast();
  const location = useLocation();
  const detail = useAiData<OrgDetail>(`/api/admin/organizations/${id}`);
  const modules = useAiData<Modules>(`/api/admin/organizations/${id}/modules`);
  // /Organizations/AbrirComoUsuario redirige aquí con la ventana abierta (por estado, no por la dirección).
  const [open, setOpen] = useState(() => (location.state as { open?: string } | null)?.open === "abrir");
  const [off, setOff] = useState<Module | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const org = detail.data;
  useOpenOrg(org ? { id: org.organizationId, name: displayName(org), code: org.code } : null);
  useTitle(org ? displayName(org) : L("ADM_COMPANY"));

  if (detail.state === "missing") return <OrgNotFound />;
  if (detail.state === "failed" && !org)
    return (
      <PageState
        head={<PageHead title={L("ADM_COMPANY")} />}
        title={L("ADM_UNAVAILABLE")}
        line={L("ADM_UNAVAILABLE_LINE")}
        action={
          <button type="button" className="tm-btn secondary" onClick={detail.retry}>
            {L("ADM_RETRY")}
          </button>
        }
      />
    );
  if (!org)
    return (
      <>
        <PageHead title={L("ADM_COMPANY")} />
        <IndicatorSkeleton count={4} label={L("ADM_HOME_INDICATORS")} />
        <RowsSkeleton rows={4} table />
      </>
    );

  const name = displayName(org);
  const status = orgStatus(L, org.status);
  const people = org.people;
  const add = `/Organizations/AgregarUsuario?id=${id}`;
  const usersHref = `/Organizations/Usuarios?id=${id}`;
  const messagingHref = `/Organizations/Bandejas?id=${id}`;

  const toggle = async (module: Module, enable: boolean) => {
    setBusy(module.code);
    const reply = await postJson<{ saved?: boolean; messageKey?: string }>(
      `/api/admin/organizations/${id}/modules/${encodeURIComponent(module.code)}/${enable ? "enable" : "disable"}`,
      {},
    );
    setBusy(null);
    if (reply.status !== 200 || !reply.body?.saved) {
      toast.show(L(reply.body?.messageKey ?? "ADMIN_ERR_GENERIC"));
      return false;
    }
    void modules.reload();
    return true;
  };
  const turnOn = async (module: Module) => {
    if (!(await toggle(module, true))) return;
    const label = moduleName(L, module.code, { [module.code]: module.name });
    // D8: encender aplica al instante con «Deshacer».
    toast.show(formatText(L("ADM_ORGS_MODULE_ON"), label), {
      label: L("ADM_UNDO"),
      run: () => void toggle(module, false),
    });
  };

  const moduleSwitch = (module: Module, hideText = false) => (
    <CellSwitch
      hideText={hideText}
      className={styles.switchCell}
      trackClass={styles.track}
      checked={module.enabled}
      label={moduleName(L, module.code, { [module.code]: module.name })}
      text={L(module.enabled ? "ADM_ORGS_ON" : "ADM_ORGS_OFF")}
      disabled={module.locked || busy === module.code}
      onChange={(value) => (value ? void turnOn(module) : setOff(module))}
    />
  );
  // E84: el número lleva a la lista con el filtro en la dirección y el mismo número en el destino; solo con el módulo
  // encendido y alguien que lo tenga.
  const usersTarget = (module: Module) =>
    !module.locked && module.enabled && module.usersCount
      ? `${usersHref}&module=${encodeURIComponent(module.code)}`
      : undefined;
  const usersText = (module: Module): ReactNode =>
    // Chat no se concede por módulo (va con la bandeja): su fila dice por qué, nunca "no disponible".
    module.locked ? (
      L("ADM_ORGS_MODULE_CHAT_ALWAYS")
    ) : module.usersCount == null ? (
      <span title={L("ADM_ORGS_NOT_AVAILABLE")}>—</span>
    ) : !module.enabled && module.usersCount === 0 ? (
      "—"
    ) : (
      plural(L, locale, module.usersCount, "ADM_N_USERS_ONE", "ADM_N_USERS")
    );
  const usersCell = (module: Module): ReactNode => {
    const target = usersTarget(module);
    return target ? (
      <a className={styles.usersLink} href={target}>
        {usersText(module)}
      </a>
    ) : (
      usersText(module)
    );
  };

  return (
    <>
      <PageHead
        title={name}
        titleTag={<span className="tm-tag">{status.text}</span>}
        sub={formatText(L("ADM_ORGS_SUMMARY_SUB"), org.legalName, org.code)}
      >
        {org.canImpersonate ? (
          <RowMenu
            label={L("ADM_MORE_ACTIONS")}
            items={[{ label: L("ADM_PPL_OPEN_AS_MENU"), onSelect: () => setOpen(true) }]}
          />
        ) : null}
        <a className={`tm-btn secondary ${ui.desktopInline}`} href={`/Organizations/Simulator?id=${id}`}>
          {L("ADM_ORGS_SIMULATE")}
        </a>
        <a className={`tm-btn primary ${ui.desktopInline}`} href={add}>
          {L("ORG_ADD_USER")}
        </a>
      </PageHead>
      {people && people.total === 0 ? (
        <Notice
          tone="warning"
          action={
            <a className="tm-btn secondary" href={add}>
              {L("NPA_ADD_FIRST_USER")}
            </a>
          }
        >
          {L("NPA_ORG_NO_USERS")}
        </Notice>
      ) : null}
      <Indicators label={L("ADM_HOME_INDICATORS")}>
        <Indicator
          label={L("ADM_ORGS_KPI_USERS")}
          href={usersHref}
          value={people ? people.total.toLocaleString(locale) : "—"}
          line={
            people
              ? formatText(
                  L("ADM_ORGS_KPI_USERS_LINE"),
                  people.active.toLocaleString(locale),
                  people.blocked.toLocaleString(locale),
                )
              : L("ADM_ORGS_NOT_AVAILABLE")
          }
          muted={!people}
        />
        <Indicator
          label={L("ADM_ORGS_KPI_OWNER")}
          href={`/Organizations/Propietario?id=${id}`}
          small
          value={people ? (people.owner?.displayName ?? L("ADM_PPL_NO_OWNER_SHORT")) : "—"}
          line={
            !people
              ? L("ADM_ORGS_NOT_AVAILABLE")
              : people.owner
                ? formatText(
                    L("ADM_ORGS_KPI_OWNER_LINE"),
                    people.owner.lastLoginAt
                      ? whenIn(L, locale, people.owner.lastLoginAt, org.timezone, true)
                      : L("ADM_PPL_NEVER_LOWER"),
                  )
                : L("ADM_PPL_NO_OWNER_LINE")
          }
          muted={!people || !people.owner}
        />
        <Indicator
          label={L("ADM_ORGS_KPI_INBOXES")}
          href={messagingHref}
          value={org.inboxes ? org.inboxes.active.toLocaleString(locale) : "—"}
          line={
            org.inboxes
              ? plural(L, locale, org.inboxes.total, "ADM_ORGS_OF_INBOXES_ONE", "ADM_ORGS_OF_INBOXES")
              : L("ADM_ORGS_NOT_AVAILABLE")
          }
          muted={!org.inboxes}
        />
        <Indicator
          label={L("ADM_ORGS_KPI_CHANNELS")}
          href={`/Organizations/Canales?id=${id}`}
          value={org.channels ? org.channels.active.toLocaleString(locale) : "—"}
          line={
            org.channels
              ? plural(L, locale, org.channels.total, "ADM_ORGS_OF_CHANNELS_ONE", "ADM_ORGS_OF_CHANNELS")
              : L("ADM_ORGS_NOT_AVAILABLE")
          }
          muted={!org.channels}
        />
      </Indicators>
      <Section id="org-modules" title={L("ADM_ORGS_MODULES")} line={L("ADM_ORGS_MODULES_LINE")}>
        {modules.state === "loading" && !modules.data ? (
          <RowsSkeleton rows={4} table />
        ) : !modules.data ? (
          <Notice
            tone="error"
            action={
              <button type="button" className="tm-btn secondary" onClick={modules.retry}>
                {L("ADM_RETRY")}
              </button>
            }
          >
            {L("ADM_UNAVAILABLE_LINE")}
          </Notice>
        ) : modules.data.items.length === 0 ? (
          <div className="tm-empty" role="status">
            <strong>{L("ADM_ORGS_NO_MODULES")}</strong>
          </div>
        ) : (
          <SimpleTable
            label={L("ADM_ORGS_MODULES")}
            className={styles.settings}
            part="tabla-ajustes"
            rows={modules.data.items}
            rowKey={(module) => module.code}
            columns={[
              {
                key: "module",
                title: L("ADM_ORGS_COL_MODULE"),
                render: (module) => <strong>{moduleName(L, module.code, { [module.code]: module.name })}</strong>,
              },
              { key: "status", title: L("ADM_ORGS_COL_STATUS"), render: (module) => moduleSwitch(module) },
              { key: "users", title: L("ADM_ORGS_COL_USERS"), numeric: true, render: (module) => usersCell(module) },
            ]}
            mobile={(module) => ({
              title: moduleName(L, module.code, { [module.code]: module.name }),
              // Celular (referencia): «Encendido · 14 usuarios» en la línea y el interruptor al final. El número va como
              // texto y la fila entera lleva a la lista (C28 en el celular): la fila no crece por la zona táctil.
              line: (
                <>
                  {L(module.enabled ? "ADM_ORGS_ON" : "ADM_ORGS_OFF")}
                  {module.locked || module.usersCount ? <> · {usersText(module)}</> : null}
                </>
              ),
              href: usersTarget(module),
              end: moduleSwitch(module, true),
            })}
          />
        )}
      </Section>
      <Section id="org-region" title={L("ADM_ORGS_REGION")}>
        <dl className={ui.facts} data-part="datos">
          <div>
            <dt>{L("ORG_COUNTRY")}</dt>
            <dd>{countryName(L, org.countryCode)}</dd>
          </div>
          <div>
            <dt>{L("ORG_TIMEZONE")}</dt>
            <dd>{timezoneLabel(org.timezone)}</dd>
          </div>
          <div>
            <dt>{L("ORG_LANGUAGE")}</dt>
            <dd>{languageName(L, org.defaultLanguageCode)}</dd>
          </div>
          <div>
            <dt>{L("ORG_CURRENCY")}</dt>
            <dd>{currencyName(L, org.defaultCurrencyCode)}</dd>
          </div>
        </dl>
      </Section>
      <MobileActions>
        <a className="tm-btn secondary" href={`/Organizations/Simulator?id=${id}`}>
          {L("ADM_ORGS_SIMULATE")}
        </a>
        <a className="tm-btn primary" href={add}>
          {L("ORG_ADD_USER")}
        </a>
      </MobileActions>
      <ConfirmWindow
        open={!!off}
        title={formatText(L("ADM_ORGS_MODULE_OFF_TITLE"), off ? moduleName(L, off.code, { [off.code]: off.name }) : "")}
        line={
          off
            ? off.usersCount
              ? formatText(
                  L(off.usersCount === 1 ? "ADM_ORGS_MODULE_OFF_LINE_ONE" : "ADM_ORGS_MODULE_OFF_LINE"),
                  off.usersCount.toLocaleString(locale),
                  moduleName(L, off.code, { [off.code]: off.name }),
                  name,
                )
              : formatText(L("ADM_ORGS_MODULE_OFF_LINE_NONE"), moduleName(L, off.code, { [off.code]: off.name }), name)
            : ""
        }
        action={L("ADM_ORGS_MODULE_OFF")}
        onClose={() => setOff(null)}
        onConfirm={async () => {
          const module = off!;
          if (!(await toggle(module, false))) return false;
          toast.show(
            formatText(L("ADM_ORGS_MODULE_OFF_DONE"), moduleName(L, module.code, { [module.code]: module.name })),
          );
          return true;
        }}
      />
      {org.canImpersonate ? (
        <ImpersonateWindow orgId={id} open={open} person={null} onClose={() => setOpen(false)} />
      ) : null}
    </>
  );
}
